import {
    Component,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { UserFacadeService } from '@wdx/clmi/api-services/services';
import { NavItem } from '@wdx/shared/utils';
import { take } from 'rxjs/operators';
import { DropdownButtonStyle } from '../../../../models/dropdown-button-style.model';
import * as rootReducer from '../../../../state/_setup/reducers';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'molecule-context-menu-dropdown',
    templateUrl: './molecule-context-menu-dropdown.component.html',
    standalone: false,
})
export class MoleculeContextMenuDropdownComponent implements OnChanges {
    private store$ = inject(Store<rootReducer.State>);
    private userFacadeService = inject(UserFacadeService);

    @Input() menu: NavItem[];
    @Input() menuTitle: string;
    @Input() buttonStyle: DropdownButtonStyle = DropdownButtonStyle.Result;
    @Input() placement = 'bottom-left';
    @Input() btnClass = 'btn-link';
    @Input() altText: string;
    @Input() tooltip?: string;
    @Input() isMulti: boolean;
    @Input() autoClose: boolean | 'outside' | 'inside' = true;
    @Input() hasSearch: boolean;
    @Input() highlightSelected = true;
    @Input() size = 'auto';
    @Input() parentContainer: string;
    @Input() hideDropdownChevron: boolean;
    @Input() initialValue: string | string[];
    @Input() defaultSelectFirst: boolean;
    @Input() disabled: boolean;
    @Input() isLoading: boolean;
    @Input() cySelector: string;

    @Output() singleItemSelected = new EventEmitter<NavItem>();
    @Output() multipleItemsSelected = new EventEmitter<NavItem[]>();
    @Output() toggled = new EventEmitter<boolean>();

    selectedMenuItems: NavItem[] = [];
    allMenuItems: NavItem[] = [];
    showButton = true;

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.menu && changes.menu.isFirstChange()) {
            this.allMenuItems = changes.menu.currentValue;
        }

        if (this.menu && (this.initialValue || this.defaultSelectFirst)) {
            this.emitInitialValue();
        }
        if (this.menu?.length && this.menu?.every((item) => item?.privilege)) {
            this.setPrivilegeDropdownMenuItem();
        } else {
            this.showButton = true;
        }
    }

    setPrivilegeDropdownMenuItem() {
        this.showButton = false;
        this.userFacadeService
            .getUserPrivilege$()
            .pipe(take(1))
            .subscribe((userPrivilege) => {
                const showButton =
                    this.userFacadeService.isAdmin ||
                    this.menu?.some((menuItem) => {
                        return userPrivilege?.includes(menuItem.privilege);
                    });

                this.showButton = showButton;
            });
    }

    onDropdownToggled(isOpen: boolean) {
        this.toggled.emit(isOpen);
    }

    emitInitialValue() {
        const initialValueArray = this.initialValue
            ? Array.isArray(this.initialValue)
                ? this.initialValue
                : [this.initialValue]
            : this.menu[0].subMenu
              ? [this.menu[0].subMenu[0].value]
              : [this.menu[0].value];

        let initialSelectedMenuItems = [];
        this.menu.forEach((menuItem) => {
            if (initialValueArray.includes(menuItem.value)) {
                initialSelectedMenuItems.push(menuItem);
            }
            initialSelectedMenuItems = [
                ...initialSelectedMenuItems,
                ...(menuItem.subMenu?.length
                    ? menuItem.subMenu.filter((subItem) =>
                          initialValueArray.includes(subItem.value),
                      )
                    : []),
            ];
        });

        this.selectedMenuItems = initialSelectedMenuItems;

        if (this.isMulti) {
            this.multipleItemsSelected.emit(this.selectedMenuItems);
        } else {
            this.singleItemSelected.emit({
                ...this.selectedMenuItems[0],
                isInitialValue: true,
            });
        }

        if (this.defaultSelectFirst) {
            this.filterSelectedValues();
        }
    }

    onSingleItemSelected(navItem: NavItem): void {
        this.selectedMenuItems = [navItem];
        this.singleItemSelected.emit({
            ...navItem,
            isInitialValue: false,
        });

        if (navItem.action) {
            this.store$.dispatch(navItem.action);
        }

        if (this.defaultSelectFirst) {
            this.filterSelectedValues();
        }
    }

    onMultipleItemsSelected(menuItems: NavItem[]): void {
        this.selectedMenuItems = menuItems;
        this.multipleItemsSelected.emit(menuItems);
    }

    filterSelectedValues() {
        const selectedValues = this.selectedMenuItems.map(
            (selectedMenu) => selectedMenu.value,
        );
        this.menu = this.allMenuItems.filter(
            (navItem) => !selectedValues.includes(navItem.value),
        );
    }
}
